import { create } from 'zustand';
import { PLANS, CREDIT_PACKS, type PricingPlan } from '@/lib/lemonsqueezy';
import api from '@/lib/api';

const FREE_PLAN: PricingPlan = {
  id: 'free',
  name: 'Free',
  description: 'Starter plan',
  price: 0,
  priceId: 'free',
  credits: 0,
  features: ['Basic access'],
};

export interface CreditTransaction {
  id: string;
  amount: number;
  type: 'spend' | 'subscription_grant' | 'subscription_reset' | 'pack_purchase' | 'bonus' | 'refund';
  actionType?: string;
  modelSlug?: string;
  inputTokens?: number;
  outputTokens?: number;
  description: string;
  createdAt: string;
}

export interface Invoice {
  id: string;
  amount: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  invoiceDate: string;
  paidAt?: string;
  pdfUrl?: string;
}

export interface BillingPlan {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  creditsMonthly: number;
  trialCredits: number;
  personasLimit: number;
  targetsLimit: number;
  priceMonthlyCents: number;
  lsVariantId: string | null;
  billingCycle: 'weekly' | 'monthly' | 'annual';
  providerKey: string;
}

export interface CreditPackProduct {
  id: string;
  slug: string;
  name: string;
  price: number;
  currency: string;
  creditsAmount: number;
  lsVariantId: string | null;
}

export interface Subscription {
  id: string;
  planId: string | null;
  status: 'active' | 'past_due' | 'canceled' | 'unpaid' | 'paused';
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

interface BillingState {
  currentPlan: PricingPlan;
  subscription: Subscription | null;
  transactions: CreditTransaction[];
  invoices: Invoice[];
  plansCatalog: BillingPlan[];
  creditPacksCatalog: CreditPackProduct[];
  creditsTotal: number;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  setPlan: (planId: string) => void;
  purchaseCredits: (packId: string) => Promise<void>;
  upgradePlan: (planId: string) => Promise<void>;
  createCheckout: (type: 'plan' | 'credit_pack', slug: string) => Promise<void>;
  cancelSubscription: () => Promise<void>;
  fetchTransactions: () => Promise<void>;
  fetchInvoices: () => Promise<void>;
  fetchBillingSummary: () => Promise<void>;
  fetchCatalog: () => Promise<void>;
  getRemainingCredits: () => number;
}

interface BillingSubscriptionResponse {
  id: string;
  plan_id?: string | null;
  status: Subscription['status'];
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean | null;
}

interface BillingSummaryResponse {
  data: {
    plan: {
      slug: string | null;
    };
    credits: {
      total: number;
    };
    subscription: BillingSubscriptionResponse | null;
    plans?: BillingCatalogResponse['data']['plans'];
    credit_packs?: BillingCatalogResponse['data']['credit_packs'];
  };
}

interface BillingTransactionsResponse {
  data: Array<{
    id: string;
    credits_used: number;
    action_type: string;
    metadata?: {
      input_tokens?: number;
      output_tokens?: number;
      model_slug?: string;
    } | null;
    description: string | null;
    created_at: string;
  }>;
}

interface BillingInvoicesResponse {
  data: Array<{
    id: string;
    amount_cents: number;
    currency: string;
    status: Invoice['status'];
    invoice_date: string;
    paid_at: string | null;
    invoice_pdf_url: string | null;
  }>;
}

interface BillingCatalogResponse {
  data: {
    plans: Array<{
      id: string;
      slug: string;
      name: string;
      description: string | null;
      credits_monthly: number;
      trial_credits?: number;
      personas_limit: number;
      targets_limit: number;
      price_monthly_cents: number;
      ls_variant_id: string | null;
      billing_cycle?: 'weekly' | 'monthly' | 'annual';
      provider_key?: string;
    }>;
    credit_packs: Array<{
      id: string;
      slug: string;
      name: string;
      price: number;
      currency: string;
      credits_amount: number;
      ls_variant_id: string | null;
    }>;
  };
}

function parseApiError(error: unknown, fallback: string): string {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: unknown }).response === 'object'
  ) {
    const response = (error as {
      response?: { data?: { error?: string; message?: string; detail?: string } };
    }).response;
    return response?.data?.error ?? response?.data?.message ?? response?.data?.detail ?? fallback;
  }
  return fallback;
}

function mapPlanSlugToPlan(planSlug: string | null): PricingPlan {
  if (!planSlug || planSlug === 'free') {
    return FREE_PLAN;
  }
  const found = PLANS.find((plan) => plan.id.startsWith(planSlug));
  return found ?? FREE_PLAN;
}

function mapSubscription(item: BillingSubscriptionResponse | null): Subscription | null {
  if (!item) {
    return null;
  }
  return {
    id: item.id,
    planId: item.plan_id ?? null,
    status: item.status,
    currentPeriodStart: item.current_period_start,
    currentPeriodEnd: item.current_period_end,
    cancelAtPeriodEnd: item.cancel_at_period_end ?? false,
  };
}

export const useBillingStore = create<BillingState>((set, get) => ({
  currentPlan: FREE_PLAN,
  subscription: null,
  transactions: [],
  invoices: [],
  plansCatalog: [],
  creditPacksCatalog: [],
  creditsTotal: 0,
  isLoading: false,
  error: null,

  setPlan: (planId) => {
    const plan = PLANS.find(p => p.id === planId);
    if (plan) {
      set({ currentPlan: plan });
    }
  },

  purchaseCredits: async (packId) => {
    set({ isLoading: true, error: null });
    try {
      const pack = CREDIT_PACKS.find(p => p.id === packId);
      if (!pack) {
        throw new Error('Credit pack not found');
      }
      const transaction: CreditTransaction = {
        id: Math.random().toString(36),
        amount: pack.credits,
        type: 'pack_purchase',
        description: `Purchased ${pack.credits} credits`,
        createdAt: new Date().toISOString(),
      };

      set(state => ({
        transactions: [transaction, ...state.transactions],
        creditsTotal: state.creditsTotal + pack.credits,
        isLoading: false,
      }));
    } catch (error: unknown) {
      const parsedError = parseApiError(error, 'Unable to process credit purchase');
      set({ isLoading: false, error: parsedError });
      throw new Error(parsedError);
    }
  },

  upgradePlan: async (planId) => {
    set({ isLoading: true, error: null });
    try {
      const plan = PLANS.find(p => p.id === planId);
      if (!plan) {
        throw new Error('Plan not found');
      }
      const subscription: Subscription = {
        id: Math.random().toString(36),
        planId: plan.id,
        status: 'active',
        currentPeriodStart: new Date().toISOString(),
        currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        cancelAtPeriodEnd: false,
      };

      set({
        currentPlan: plan,
        subscription,
        isLoading: false,
      });
    } catch (error: unknown) {
      const parsedError = parseApiError(error, 'Unable to upgrade plan');
      set({ isLoading: false, error: parsedError });
      throw new Error(parsedError);
    }
  },


  createCheckout: async (type, slug) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: { checkout_url: string; mode: 'placeholder' | 'live'; message?: string } }>('/billing/checkout', {
        type,
        slug,
      });
      const checkoutUrl = response.data.data.checkout_url;
      if (!checkoutUrl) {
        throw new Error('Checkout URL missing from billing response');
      }
      window.open(checkoutUrl, '_blank', 'width=600,height=760');
      set({ isLoading: false });
    } catch (error: unknown) {
      const parsedError = parseApiError(error, 'Unable to create checkout');
      set({ isLoading: false, error: parsedError });
      throw new Error(parsedError);
    }
  },

  cancelSubscription: async () => {
    set({ isLoading: true, error: null });
    try {
      set(state => ({
        subscription: state.subscription ? {
          ...state.subscription,
          cancelAtPeriodEnd: true,
        } : null,
        isLoading: false,
      }));
    } catch (error: unknown) {
      const parsedError = parseApiError(error, 'Unable to cancel subscription');
      set({ isLoading: false, error: parsedError });
      throw new Error(parsedError);
    }
  },

  fetchBillingSummary: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<BillingSummaryResponse>('/billing/summary');
      const plan = mapPlanSlugToPlan(response.data.data.plan.slug);

      set((state) => ({
        currentPlan: plan,
        subscription: mapSubscription(response.data.data.subscription),
        creditsTotal: response.data.data.credits.total,
        plansCatalog: typeof response.data.data.plans === 'undefined' ? state.plansCatalog : response.data.data.plans.map((item) => ({
          id: item.id,
          slug: item.slug,
          name: item.name,
          description: item.description,
          creditsMonthly: item.credits_monthly,
          trialCredits: item.trial_credits ?? 0,
          personasLimit: item.personas_limit,
          targetsLimit: item.targets_limit,
          priceMonthlyCents: item.price_monthly_cents,
          lsVariantId: item.ls_variant_id,
          billingCycle: item.billing_cycle ?? 'monthly',
          providerKey: item.provider_key ?? 'migration_fallback',
        })),
        creditPacksCatalog: typeof response.data.data.credit_packs === 'undefined' ? state.creditPacksCatalog : response.data.data.credit_packs.map((item) => ({
          id: item.id,
          slug: item.slug,
          name: item.name,
          price: item.price,
          currency: item.currency,
          creditsAmount: item.credits_amount,
          lsVariantId: item.ls_variant_id,
        })),
        isLoading: false,
      }));
    } catch (error: unknown) {
      const parsedError = parseApiError(error, 'Failed to fetch billing summary');
      set({ isLoading: false, error: parsedError });
    }
  },

  fetchCatalog: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<BillingCatalogResponse>('/billing/catalog');
      const plansCatalog: BillingPlan[] = (response.data.data.plans ?? []).map((item) => ({
        id: item.id,
        slug: item.slug,
        name: item.name,
        description: item.description,
        creditsMonthly: item.credits_monthly,
        trialCredits: item.trial_credits ?? 0,
        personasLimit: item.personas_limit,
        targetsLimit: item.targets_limit,
        priceMonthlyCents: item.price_monthly_cents,
        lsVariantId: item.ls_variant_id,
        billingCycle: item.billing_cycle ?? 'monthly',
        providerKey: item.provider_key ?? 'migration_fallback',
      }));
      const creditPacksCatalog: CreditPackProduct[] = (response.data.data.credit_packs ?? []).map((item) => ({
        id: item.id,
        slug: item.slug,
        name: item.name,
        price: item.price,
        currency: item.currency,
        creditsAmount: item.credits_amount,
        lsVariantId: item.ls_variant_id,
      }));

      set({ plansCatalog, creditPacksCatalog, isLoading: false });
    } catch (error: unknown) {
      const parsedError = parseApiError(error, 'Failed to fetch billing catalog');
      set({ isLoading: false, error: parsedError });
    }
  },

  fetchTransactions: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<BillingTransactionsResponse>('/billing/transactions');
      const transactions: CreditTransaction[] = (response.data.data ?? []).map((item) => ({
        id: item.id,
        amount: item.credits_used,
        type: item.credits_used < 0 ? 'bonus' : 'spend',
        actionType: item.action_type,
        modelSlug: item.metadata?.model_slug,
        inputTokens: item.metadata?.input_tokens,
        outputTokens: item.metadata?.output_tokens,
        description:
          item.description ??
          `${item.action_type}${
            item.metadata?.input_tokens || item.metadata?.output_tokens
              ? ` (${item.metadata?.input_tokens ?? 0} in / ${item.metadata?.output_tokens ?? 0} out tokens)`
              : ''
          }`,
        createdAt: item.created_at,
      }));
      set({ transactions, isLoading: false });
    } catch (error: unknown) {
      const parsedError = parseApiError(error, 'Failed to fetch transactions');
      set({ isLoading: false, error: parsedError });
    }
  },

  fetchInvoices: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<BillingInvoicesResponse>('/billing/invoices');
      const invoices: Invoice[] = (response.data.data ?? []).map((item) => ({
        id: item.id,
        amount: item.amount_cents,
        currency: item.currency,
        status: item.status,
        invoiceDate: item.invoice_date,
        paidAt: item.paid_at ?? undefined,
        pdfUrl: item.invoice_pdf_url ?? undefined,
      }));
      set({ invoices, isLoading: false });
    } catch (error: unknown) {
      const parsedError = parseApiError(error, 'Failed to fetch invoices');
      set({ isLoading: false, error: parsedError });
    }
  },

  getRemainingCredits: () => {
    return get().creditsTotal;
  },
}));
